import bitmapFontXml from "../../../ui/generated/fonts/pixel.fnt" with { type: "text" };
import { advancesFrom } from "../../../ui/kit/pixel-text.ts";

/**
 * What a chat bubble can draw of a line (P1.9-T23, ADR-068 decisions 2 and 9, ADR-061 amendment of 2026-09-25). The
 * chat rule keeps every script, emoji, combining mark and joiner, and the chat log in the DOM shows them all, but the
 * canvas draws only the pixel face, which holds the Latin and Greek glyphs Pixelify Sans ships, four Greek letters it
 * lacks and no combining mark, and never falls back to another font. A bitmap font cannot place a mark on a letter, so a line is first put in
 * composed form (NFC): a letter typed as a base and a combining mark becomes the one letter the face holds. Phaser's
 * `BitmapText` skips a character its font lacks, so a line in another script would draw as nothing; a bubble draws
 * each code point the face still lacks (Cyrillic, CJK, an emoji, a mark with no composed form) as U+00B7 instead, so
 * a line shows that its speaker spoke. Composing happens here alone: the chat rule, the relay and the log keep the
 * text as typed. The glyphs kept are read from the bitmap font the client bundles, so a face that gains a glyph draws
 * it without a change here.
 */

/**
 * What a bubble draws in place of a code point the pixel face lacks: U+00B7, the middle dot.
 */
export const MISSING_GLYPH = String.fromCodePoint(0xb7);

/**
 * The code points the pixel face holds, each with its advance, of which only the keys are read here.
 */
const FACE: ReadonlyMap<number, number> = advancesFrom(bitmapFontXml);

/**
 * A line as a bubble draws it: put in composed form, then every code point the pixel face holds kept and every
 * other code point drawn as `MISSING_GLYPH`. A character written as a surrogate pair is one code point, so one dot,
 * and so is a surrogate met alone.
 *
 * @param text The line's text as the chat rule leaves it.
 * @returns The text in the face's glyphs, as many code points long as the line in composed form.
 * @example
 * bubbleGlyphs(`cafe${String.fromCodePoint(0x301)} ${String.fromCodePoint(0x1f600)}`);
 * // "café", a space and U+00B7
 */
export function bubbleGlyphs(text: string): string {
  let drawn = "";
  for (const character of text.normalize("NFC")) {
    drawn += FACE.has(character.codePointAt(0) as number) ? character : MISSING_GLYPH;
  }
  return drawn;
}
